/* GASSISA
=========================================================================================
* Descripcion: Consulta de fallos locales del sistema
* Revisión 18/09/2026 | José Luis Aragón Cervantes | Flujo Kanban, seguimiento y privacidad.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revision   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
 *      1       | 31/08/2026 | Lic. Jose Luis Aragon Cervantes  | Visor SA de errores locales y envio WhatsApp
 *      2       | 31/08/2026 | Lic. Jose Luis Aragon Cervantes  | Boton Home estandar para regresar al dashboard
 *      3       | 18/09/2026 | LIC. José Luis Aragón C.                       | Acceso SA + ADMINISTRADOR + SUPERVISOR
 * ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeProvider, createTheme, Alert, Box, Button, Card, CardContent, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import HomeIcon from "@mui/icons-material/Home";
import RefreshIcon from "@mui/icons-material/Refresh";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";

import SizeSoftXLayoutForm from "../leyout/SizeSoftXLayoutForm.jsx";
import TicketsSeguimiento from "./TicketsSeguimiento.jsx";
import { useSesion } from "../../context/System/SesionContext";
import { useRequest } from "../../context/System/RequestContext";
import { useLayout } from "../../context/System/LayoutContext";
import { apiWhatsAppEnviar, apiSoporteTickets } from "../../settings/ApiConfig";
import { getAuthorizationToken } from "../../settings/sessionStorage";
import {
  buildErrorLogWhatsAppMessage,
  clearErrorLogs,
  getErrorLogs,
  removeErrorLog,
} from "../../settings/errorLogStorage";

// 18/09/2026 | José Luis Aragón Cervantes | Apariencia Jira local, sin cambios de flujo.
const fallosTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#579dff" },
    secondary: { main: "#28292d" },
    background: { default: "#1f2023", paper: "#28292d" },
    text: { primary: "#dfe1e6", secondary: "#a6adba" },
    divider: "#41444b",
  },
  typography: { fontFamily: '"Segoe UI", Roboto, Arial, sans-serif' },
  shape: { borderRadius: 10 },
  components: {
    MuiCard: { styleOverrides: { root: { backgroundImage: "none", boxShadow: "none", border: "1px solid #41444b", textAlign: "left" } } },
    MuiCardContent: { styleOverrides: { root: { padding: 20 } } },
    MuiTypography: { styleOverrides: { root: { textAlign: "left" } } },
    MuiButton: { styleOverrides: { root: { textTransform: "none", fontWeight: 600, boxShadow: "none" } } },
    MuiOutlinedInput: { styleOverrides: { root: { backgroundColor: "#222327", color: "#dfe1e6" }, notchedOutline: { borderColor: "#565b66" } } },
    MuiDialog: { styleOverrides: { paper: { backgroundImage: "none", border: "1px solid #41444b", borderRadius: 12, color: "#dfe1e6" } } },
    MuiDialogTitle: { styleOverrides: { root: { fontSize: "1.2rem", fontWeight: 600, borderBottom: "1px solid #41444b", padding: "20px 24px" } } },
    MuiDialogContent: { styleOverrides: { root: { "&&": { paddingTop: 24 }, textAlign: "left" } } },
    MuiDialogActions: { styleOverrides: { root: { padding: "16px 24px", borderTop: "1px solid #41444b", flexWrap: "wrap", gap: 8 } } },
  },
});

function FallosAppearance({ children }) {
  return <ThemeProvider theme={fallosTheme}><Box sx={{
    bgcolor: "background.default", color: "text.primary", minHeight: "100%", textAlign: "left",
    "& .work, & .work-area, & .elements": { backgroundColor: "#1f2023", color: "#dfe1e6" },
    "& .title.MuiAppBar-root": { background: "#28292d", color: "#dfe1e6", boxShadow: "none", borderBottom: "1px solid #41444b" },
    "& .MuiToolbar-root": { flexWrap: "wrap", paddingTop: 1, paddingBottom: 1, gap: 1 },
    "& .icon": { color: "#dfe1e6", fontFamily: '"Segoe UI", Roboto, Arial, sans-serif', fontSize: "inherit", margin: 0 },
    "& .MuiIconButton-root": { borderRadius: 2, gap: 0.5 },
    "& .MuiIconButton-root.Mui-disabled .icon": { color: "#737985" },
  }}>{children}</Box></ThemeProvider>;
}

const getLocalDate = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const normalizePhone = (value) => {
  let tel = String(value || "").replace(/[^0-9]/g, "");
  if (tel.length === 12 && tel.startsWith("52")) tel = tel.slice(2);
  return tel;
};

const isValidPhone = (value) => normalizePhone(value).length === 10;

export default function FrmRegistroFallos() {
  const { tipoUsuario, usuarioData, parametrosCajaForm } = useSesion();
  const { PostRequest } = useRequest();
  const { handleOpenAlert } = useLayout();
  const navigate = useNavigate();

  const handleClickRegresarMainDashboard = () => navigate("/*");

  const [logs, setLogs] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filter, setFilter] = useState("");
  const [openConfirmClear, setOpenConfirmClear] = useState(false);
  const [openWhatsApp, setOpenWhatsApp] = useState(false);
  const [telefono, setTelefono] = useState("");
  const [nombreContacto, setNombreContacto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [openTicket, setOpenTicket] = useState(false);
  const [tTitulo, setTTitulo] = useState("");
  const [tCategoria, setTCategoria] = useState("SOPORTE TECNICO");
  const [tPrioridad, setTPrioridad] = useState("MEDIA");
  const [tDescripcion, setTDescripcion] = useState("");
  const [creandoTicket, setCreandoTicket] = useState(false);
  const [ultimoFolio, setUltimoFolio] = useState("");
  const [tab, setTab] = useState(0);
  const [ticketsRefreshKey, setTicketsRefreshKey] = useState(0);

  const loadLogs = () => {
    setLogs(getErrorLogs());
    setSelectedIds([]);
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const filteredLogs = useMemo(() => {
    const text = filter.trim().toLowerCase();
    if (!text) return logs;

    return logs.filter((log) =>
      [
        log.fecha,
        log.modulo,
        log.origen,
        log.metodo,
        log.httpCode,
        log.ruta,
        log.pantalla,
        log.mensaje,
        log.pista,
      ]
        .join(" ")
        .toLowerCase()
        .includes(text)
    );
  }, [filter, logs]);

  const selectedLogs = useMemo(() => {
    if (!selectedIds.length) return filteredLogs.slice(0, 10);
    const ids = new Set(selectedIds);
    return logs.filter((log) => ids.has(log.id));
  }, [filteredLogs, logs, selectedIds]);

  const toggleSelected = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleDeleteOne = (id) => {
    setLogs(removeErrorLog(id));
    setSelectedIds((prev) => prev.filter((item) => item !== id));
  };

  const handleClearAll = () => {
    setLogs(clearErrorLogs());
    setSelectedIds([]);
    setOpenConfirmClear(false);
  };

  const handleEnviarWhatsApp = async () => {
    const tel = normalizePhone(telefono);
    if (tel.length !== 10) {
      handleOpenAlert("Ingrese un numero de telefono valido de 10 digitos", "error");
      return;
    }

    if (!selectedLogs.length) {
      handleOpenAlert("No hay registros para enviar", "warning");
      return;
    }

    if (!parametrosCajaForm?.idphonewhatsapp?.trim() || !parametrosCajaForm?.tokenaccesowhatsapp?.trim()) {
      handleOpenAlert("WhatsApp no esta configurado en los parametros de esta caja", "error");
      return;
    }

    setEnviando(true);
    let response;
    try {
      response = await PostRequest({
        url: apiWhatsAppEnviar,
        body: {
          to: `52${tel}`,
          nombre: nombreContacto,
          caja: parametrosCajaForm?.caja ?? "",
          mensaje: buildErrorLogWhatsAppMessage(selectedLogs),
          guardarContacto: false,
          idphonewhatsapp: parametrosCajaForm.idphonewhatsapp.trim(),
          tokenaccesowhatsapp: parametrosCajaForm.tokenaccesowhatsapp.trim(),
        },
        loader: "Enviando monitoreo por WhatsApp...",
      });
    } finally {
      setEnviando(false);
    }

    if (!response || response.hasError) {
      handleOpenAlert(response?.message || "Error al enviar mensaje", "error");
      return;
    }

    handleOpenAlert("Registro de fallos enviado por WhatsApp", "OK");
    setOpenWhatsApp(false);
  };

  const describirLog = (log) => [
    `Fecha: ${getLocalDate(log.fecha)}`,
    `Módulo: ${log.modulo || "Sistema"}`,
    `Origen: ${log.origen || ""}`,
    `Pantalla: ${log.pantalla || ""}`,
    `Ruta: ${log.ruta || ""}`,
    `Método: ${log.metodo || ""} | HTTP: ${log.httpCode || 0}`,
    `Mensaje: ${log.mensaje || ""}`,
    `Pista: ${log.pista || ""}`,
  ].join("\n");

  const abrirTicketDesdeFallos = () => {
    if (!selectedLogs.length) {
      // 18/09/2026 | Permitir ticket manual aunque no haya registros previos.
      setTTitulo("");
      setTCategoria("SOPORTE TECNICO");
      setTPrioridad("MEDIA");
      setTDescripcion("");
      setUltimoFolio("");
      setOpenTicket(true);
      return;
    }
    const primero = selectedLogs[0];
    setTTitulo(`Fallo: ${primero.modulo || "Sistema"} - ${String(primero.mensaje || "").slice(0, 80)}`);
    setTCategoria("SOPORTE TECNICO");
    setTPrioridad("MEDIA");
    setTDescripcion(selectedLogs.map(describirLog).join("\n\n---\n\n"));
    setUltimoFolio("");
    setOpenTicket(true);
  };

  const crearTicketSoporte = async () => {
    if (!tTitulo.trim()) {
      handleOpenAlert("El título es requerido", "error");
      return;
    }
    const fd = new FormData();
    fd.append("titulo", tTitulo.trim());
    fd.append("descripcion", tDescripcion.trim());
    fd.append("categoria", tCategoria);
    fd.append("tipoTicket", "FALLO");
    fd.append("prioridad", tPrioridad);
    fd.append("usuario", usuarioData?.Usuario || usuarioData?.usuario || "");
    fd.append("rol", tipoUsuario || "");
    const token = getAuthorizationToken();
    setCreandoTicket(true);
    try {
      const apiResp = await fetch(apiSoporteTickets, {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
        body: fd,
      });
      const data = await apiResp.json().catch(() => null);
      if (!apiResp.ok || data?.hasError) {
        handleOpenAlert(data?.message || "No se pudo crear el ticket", "error");
        return;
      }
      setUltimoFolio(data?.result?.folio || "");
      setTicketsRefreshKey((k) => k + 1);
      handleOpenAlert(`Ticket ${data?.result?.folio || ""} creado`, "OK");
    } catch (e) {
      handleOpenAlert("Error al crear ticket: " + e.message, "error");
    } finally {
      setCreandoTicket(false);
    }
  };

  const botonHome = (
    <Tooltip title="Home">
      <IconButton onClick={handleClickRegresarMainDashboard}>
        <HomeIcon className="icon" /><p className="icon">Salir</p>
      </IconButton>
    </Tooltip>
  );

  const rolesPermitidosFallos = ["SA", "ADMINISTRADOR", "SUPERVISOR"];

  if (!rolesPermitidosFallos.includes(tipoUsuario)) {
    return (
      <FallosAppearance><SizeSoftXLayoutForm
        titlePage="Registro de fallos"
        buttonActions={
          <Box sx={{ display: "flex", gap: 1 }}>{botonHome}</Box>
        }
      >
        <Box sx={{ p: 2 }}>
          <Alert severity="warning">Modulo disponible solo para usuarios SA, ADMINISTRADOR o SUPERVISOR.</Alert>
        </Box>
      </SizeSoftXLayoutForm></FallosAppearance>
    );
  }

  return (
    <FallosAppearance><SizeSoftXLayoutForm
      titlePage="Registro de fallos"
      buttonActions={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {botonHome}
          <Tooltip title="Actualizar">
            <IconButton onClick={loadLogs}>
              <RefreshIcon className="icon" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Enviar por WhatsApp">
            <IconButton onClick={() => setOpenWhatsApp(true)} disabled={!selectedLogs.length}>
              <WhatsAppIcon className="icon" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Crear ticket de soporte (funciona sin registros previos)">
            <IconButton onClick={abrirTicketDesdeFallos}>
              <ConfirmationNumberIcon className="icon" /><p className="icon">Ticket</p>
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar todos">
            <IconButton onClick={() => setOpenConfirmClear(true)} disabled={!logs.length}>
              <DeleteSweepIcon className="icon" />
            </IconButton>
          </Tooltip>
        </Box>
      }
    >
      <Box sx={{ p: { xs: 2, md: 3 }, display: "grid", gap: 2, minHeight: "calc(100vh - 260px)", alignContent: "start" }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Registro de fallos" />
          <Tab label="Mis tickets de soporte" />
        </Tabs>
        {tab === 1 ? (
          <TicketsSeguimiento refreshKey={ticketsRefreshKey} />
        ) : (
        <>
        <Card>
          <CardContent sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <TextField autoComplete="off"
              label="Buscar"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              size="small"
              sx={{ minWidth: { xs: "100%", sm: 280 }, flex: 1 }}
            />
            <Chip label={`${logs.length} registro(s)`} color="primary" variant="outlined" />
            <Chip label={`${selectedIds.length || Math.min(filteredLogs.length, 10)} para WhatsApp`} color="success" variant="outlined" />
          </CardContent>
        </Card>

        {!filteredLogs.length ? (
          <Alert severity="info">No hay fallos registrados.</Alert>
        ) : (
          filteredLogs.map((log) => (
            <Card key={log.id} variant="outlined" sx={{ borderLeft: "3px solid", borderLeftColor: selectedIds.includes(log.id) ? "primary.main" : "#41444b", transition: "border-color .15s", "&:hover": { borderColor: "#758195" } }}>
              <CardContent sx={{ display: "grid", gap: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Checkbox
                      checked={selectedIds.includes(log.id)}
                      onChange={() => toggleSelected(log.id)}
                      size="small"
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {log.modulo || "Sistema"}
                    </Typography>
                    <Chip label={`HTTP ${log.httpCode || 0}`} size="small" color={Number(log.httpCode) === 401 ? "warning" : "default"} />
                    <Chip label={log.metodo || "FETCH"} size="small" variant="outlined" />
                    <Typography variant="caption" color="text.secondary">
                      {getLocalDate(log.fecha)}
                    </Typography>
                  </Box>
                  <Tooltip title="Eliminar registro">
                    <IconButton onClick={() => handleDeleteOne(log.id)} size="small">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Typography variant="body2"><strong>Origen:</strong> {log.origen}</Typography>
                <Typography variant="body2"><strong>Pantalla:</strong> {log.pantalla}</Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}><strong>Ruta:</strong> {log.ruta}</Typography>
                <Typography variant="body2"><strong>Mensaje:</strong> {log.mensaje}</Typography>
                <Typography variant="body2" color="text.secondary"><strong>Pista:</strong> {log.pista}</Typography>
              </CardContent>
            </Card>
          ))
        )}
        </>
        )}
      </Box>

      <Dialog open={openConfirmClear} onClose={() => setOpenConfirmClear(false)}>
        <DialogTitle>Inicializar registros</DialogTitle>
        <DialogContent>
          <Typography>Se eliminaran todos los fallos almacenados localmente.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmClear(false)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleClearAll}>Eliminar todos</Button>
        </DialogActions>
      </Dialog>

            <Dialog open={openWhatsApp} onClose={() => setOpenWhatsApp(false)} maxWidth="sm" fullWidth>        <DialogTitle>Enviar fallos por WhatsApp</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 1 }}>
          <Alert severity="info">Se enviara un resumen seguro sin tokens, contrasenas ni payloads.</Alert>
          <TextField autoComplete="off"
            label="Numero de telefono"
            value={telefono}
            onChange={(e) => setTelefono(e.target.value.replace(/[^0-9+]/g, ""))}
            inputProps={{ maxLength: 14, inputMode: "tel" }}
            helperText="10 digitos o formato con 52"
          />
          <TextField autoComplete="off"
            label="Nombre del contacto"
            value={nombreContacto}
            onChange={(e) => setNombreContacto(e.target.value)}
          />
          <Typography variant="caption" color="text.secondary">
            Registros incluidos: {selectedLogs.length}. Si no selecciona registros, se toman los primeros 10 filtrados.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenWhatsApp(false)} disabled={enviando}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleEnviarWhatsApp}
            disabled={enviando || !isValidPhone(telefono)}
            startIcon={<WhatsAppIcon />}
          >
            {enviando ? "Enviando..." : "Enviar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openTicket} onClose={() => setOpenTicket(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Crear ticket de soporte desde fallos</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 1 }}>
          {selectedLogs.length ? (
            <Alert severity="info">Se crea con los {selectedLogs.length} registro(s) seleccionado(s) como detalle.</Alert>
          ) : (
            <Alert severity="info">No hay registros previos. Describa manualmente la falla para crear el ticket.</Alert>
          )}
          <TextField autoComplete="off" label="Título" value={tTitulo} onChange={(e) => setTTitulo(e.target.value)} fullWidth />
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
            <TextField autoComplete="off" label="Categoría" value={tCategoria} onChange={(e) => setTCategoria(e.target.value)} sx={{ flex: 1 }} size="small" />
            <TextField autoComplete="off" label="Prioridad" value={tPrioridad} onChange={(e) => setTPrioridad(e.target.value.toUpperCase())} sx={{ flex: 1 }} size="small" helperText="BAJA, MEDIA, ALTA, URGENTE" />
          </Box>
          <TextField autoComplete="off" label="Descripción" value={tDescripcion} onChange={(e) => setTDescripcion(e.target.value)} multiline minRows={4} fullWidth />
          {ultimoFolio && <Alert severity="success">Ticket {ultimoFolio} creado. El equipo de soporte le daré seguimiento.</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTicket(false)} disabled={creandoTicket}>Cancelar</Button>
          {ultimoFolio && <Button onClick={() => setOpenTicket(false)}>Aceptar</Button>}
          <Button variant="contained" onClick={crearTicketSoporte} disabled={creandoTicket} startIcon={<ConfirmationNumberIcon />}>
            {creandoTicket ? "Creando..." : "Crear ticket"}
          </Button>
        </DialogActions>
      </Dialog>
    </SizeSoftXLayoutForm></FallosAppearance>
  );
}
